import cn from 'clsx';
import {useTranslation} from 'react-i18next';
import {useMemo} from 'react';
import {useForm, Controller} from 'react-hook-form';
import {RatingRadioGroup, Textarea} from '@guestapp/old-ui-kit';
import {Button} from '@chekin/guestapp-ui-kit';
import {RatingStatus} from './RatingStatus';
import styles from './styles.module.css';

enum FORM_NAMES {
  rating = 'rating',
  comment = 'comment',
}

export type FormTypes = {
  [FORM_NAMES.rating]: number;
  [FORM_NAMES.comment]?: string;
};

type Props = {
  onSubmit: (data: FormTypes) => void;
  className?: string;
  rateLabel?: string;
  commentsPlaceholder?: string;
  commentsLabel?: string;
  submitLabel?: string;
  showRatingStatus?: boolean;
  rules?: {[key in FORM_NAMES]?: {required: string | boolean}};
};

function RateForm({
  onSubmit,
  className,
  rateLabel,
  commentsPlaceholder,
  commentsLabel,
  submitLabel,
  showRatingStatus = false,
  rules,
}: Props) {
  const {t} = useTranslation();
  const {
    control,
    handleSubmit,
    formState: {isValid},
  } = useForm<FormTypes>();

  const options = useMemo(
    () => [
      {label: t('feedback_modal.options.very_poor'), value: 1},
      {label: t('feedback_modal.options.poor'), value: 2},
      {label: t('feedback_modal.options.average'), value: 3},
      {label: t('feedback_modal.options.good'), value: 4},
      {label: t('feedback_modal.options.excellent'), value: 5},
    ],
    [t],
  );

  return (
    <div
      className={cn(
        'w-full flex flex-col items-center justify-center gap-8 rate-form',
        className,
      )}
    >
      <div className={cn(styles.container, 'rate-form__container')}>
        <Controller
          control={control}
          name={FORM_NAMES.rating}
          rules={rules?.[FORM_NAMES.rating]}
          render={({field}) => (
            <>
              <RatingRadioGroup
                label={rateLabel || t('rate_form.how_was_your_stay_with_us')}
                options={options}
                value={field.value}
                onChange={field.onChange}
                className={styles.RatingRadioGroup}
              />
              {showRatingStatus && (
                <RatingStatus
                  value={field.value}
                  options={options}
                  placeholder={t('feedback_modal.rating_placeholder')}
                />
              )}
            </>
          )}
        />

        <div className="w-full rate-form__textarea-wrapper">
          <div className={cn(styles.secondText, 'mb-2 rate-form__second-text')}>
            {commentsLabel || t('rate_form.add_additional_comments')}
          </div>
          <Controller
            name={FORM_NAMES.comment}
            control={control}
            rules={rules?.[FORM_NAMES.comment]}
            render={({field}) => (
              <Textarea
                {...field}
                placeholder={
                  commentsPlaceholder ||
                  t('rate_form.tell_us_what_you_liked_or_how_we_can_improve')
                }
                className="rate-form__textarea"
              />
            )}
          />
        </div>
      </div>
      <Button
        className="w-full max-w-[400px] md:w-[296px] rate-form__button rate-form__button"
        onClick={handleSubmit(onSubmit)}
        disabled={!isValid}
      >
        {submitLabel || t('rate_form.submit')}
      </Button>
    </div>
  );
}

export default RateForm;
